import { isValidGpax, canCalculateGpax } from './gpax';
import Cookies from 'js-cookie';
import config from 'config';
import _ from 'lodash';
import axios from 'axios';
import Compressor from 'compressorjs';
import moment from 'moment';

// Constants
import { CURRENT_SCHOOL_YEAR, CURRENT_YEAR_SCHOOL_TERM } from 'constants/school-year';
import fields from 'data/condition.json';
import subjectLabels from 'data/subject_labels.json';
import programCodes from 'data/programs.json';
import enabled from 'data/enabled.json';
import { getSimulatedNow, simulatedMoment, HEADER as SIMULATED_NOW_HEADER } from './simulated-clock';

const subjectMap = _.keyBy(subjectLabels, 'key');
const _ensureScore = (value) => isNaN(value) ? 0 : parseFloat(value || '0', 10);

const CEFR_RANK = { A1: 1, A2: 2, B1: 3, B2: 4, C1: 5, C2: 6 };
const _cefrLevel = (raw) => CEFR_RANK[String(raw || '').trim().toUpperCase()] || 0;

// GPAX ที่ใช้ = เลือกจาก "หลักสูตร + ปีการศึกษา" เท่านั้น — เลิกอ้าง student.school_term รายคน
//   • จบแล้ว/เด็กซิ่ว (school_year < ปีปัจจุบัน) → GPAX 6 เสมอ (derive จากปี ไม่ต้องเก็บ term ใน DB)
//   • GED/นานาชาติ/เทียบวุฒิ ตปท. (program_code > 4) → คะแนนอยู่ช่อง gpax6
//   • cohort ปีปัจจุบัน → เทอมตามฤดูกาล = CURRENT_YEAR_SCHOOL_TERM (config: ต้นฤดู 4, ~ต.ค. 5, ใกล้จบ 6)
// เดิมยึด school_term ทำให้ GED (เกรดเก็บช่อง gpax6 → deriveSchoolTerm ค้าง '6') พอสลับเป็นแกนกลาง
// ได้ gpax6 ผิด (ควร gpax4) — cohort ยังไม่จบเป็นไปไม่ได้ที่จะครบ 6 เทอม
// (สูตรเดียวกัน 3 repo ต้องตรงกัน: BE applicants.js + api-pdf folio.js)
export const gpaxCodeOf = ({ program_code, school_year } = {}) => {
  const y = Number(school_year);
  if (y && y < Number(CURRENT_SCHOOL_YEAR)) return 6;
  if (Number(program_code) > 4) return 6;
  return Number(CURRENT_YEAR_SCHOOL_TERM);
};
export const gpaxFieldOf = (info) => `gpax${gpaxCodeOf(info)}_score`;
// ช่อง GPAX ที่แฟ้มสะสมผลงานแสดง — cohort ช่วงเทอมฤดูกาล 5 แสดง GPAX4 คู่ GPAX5 เมื่อมีค่า (บางหลักสูตรกำหนด 4 ภาค
// ทั้งที่ระบบ flip แล้ว เช่น แพทย์นานาชาติ มช. เปิด 28 ต.ค.–5 พ.ย. 69) · นอกนั้นช่องเดียวตาม gpaxCodeOf
// (สูตรเดียวกับ api-pdf pdf/folio-gpax.js gpaxTermsOf)
// พิมพ์เฉพาะช่องที่มีค่า · hidden = ช่องที่เด็กติ๊กออกรายแฟ้ม (folio.gpax_hidden) แต่ต้องเหลืออย่างน้อย 1 ช่องที่มีค่า
export const gpaxTermsOf = (profile = {}, hidden) => {
  const n = gpaxCodeOf(profile);
  if (n !== 5) return [n];
  const available = [4, 5].filter((term) => isValidGpax(profile[`gpax${term}_score`]));
  const shown = available.filter((term) => !(hidden || []).includes(term));
  return shown.length ? shown : available.length ? available : [n];
};
// มี GPAX ครบ 2 ช่องถึงมีให้เลือกแสดงใน Folio Builder
export const gpaxChoicesOf = (profile = {}) => gpaxTermsOf(profile).length > 1 ? gpaxTermsOf(profile) : [];

// สละสิทธิ์ deadline text (พ.ศ.) จาก ISO ใน enabled.json — เช่น "12 มีนาคม 2570 เวลา 23.59 น."
// cancel_end ทุกตัวเป็นเวลา 23:59:59.999 ตามเขตเวลาไทย จึง hardcode "เวลา 23.59 น."
// เวลาไทยเสมอ (UTC+7) — R27-1: moment(iso) ปกติใช้เขตเวลาของเครื่อง นักเรียนที่อยู่ต่างประเทศเห็นวันสุดท้ายเลื่อน 1 วัน
export const thaiMoment = (value) => moment(value).utcOffset(420);

// "20 ม.ค. 70" — วันสุดท้ายจาก enabled.json (G26-1: เดิม hardcode วันของ TCAS69 บนหน้าชำระเงิน)
export const formatThaiShortDate = (iso) =>
  `${thaiMoment(iso).locale('th').format('D MMM')} ${String(thaiMoment(iso).year() + 543).slice(-2)}`;

export const formatThaiDeadline = (iso) =>
  `${thaiMoment(iso).locale('th').format('D MMMM')} ${thaiMoment(iso).year() + 543} เวลา 23.59 น.`;

// หน้าต่างทบทวนคะแนนตามประเภทสอบ — R28-1: เดิม ExamRecheck/ExamScores สลับด้วย comment และค้างที่ A-Level
// → หน้า TGAT/TPAT (1-6 มี.ค.) เด้งกลับ /profile และปุ่มขอทบทวนไม่ขึ้น
export const recheckWindowOf = (type) =>
  type === 'alevel' ? enabled.pbt_alevel_recheck : enabled.pbt_tgattpat_recheck;

// วัน "ปรับปรุงล่าสุด" ของเอกสาร consent (convention ตกลง 2026-07-02 — ตรงกับวันจริง
// ที่ ทปอ. ปรับเอกสารในรอบก่อนๆ): สละสิทธิ์ = 1 วันก่อน cancel_end, ยืนยันสิทธิ์ = 2 วันก่อน reply_end
const _formatThaiDateBefore = (iso, days) => {
  const d = moment(iso).subtract(days, 'day');
  return `${d.locale('th').format('D MMMM')} ${d.year() + 543}`;
};
export const formatThaiCancelUpdatedDate = (cancelEndIso) => _formatThaiDateBefore(cancelEndIso, 1);
export const formatThaiReplyUpdatedDate = (replyEndIso) => _formatThaiDateBefore(replyEndIso, 2);
export const formatThaiDate = (iso) => _formatThaiDateBefore(iso, 0);

// เก็บ auth token cookie พร้อม flag hardening:
// - secure เฉพาะ https (dev/e2e วิ่ง http://localhost → browser จะทิ้ง cookie ถ้า secure=true)
// - sameSite=lax: ไม่กระทบ flow เข้าจาก external link, กัน cross-site request บางส่วน
// httpOnly ตั้งจาก client ไม่ได้ (server เท่านั้น) — token ยัง JS อ่านได้ตาม design ที่อ่านใส่ header เอง
export const setAuthCookie = (name, value, expires) =>
  Cookies.set(name, value, {
    expires,
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
    sameSite: 'lax',
  });

const tgattpat_subjects = [
  'tgat',
  'tgat1',
  'tgat2',
  'tgat3',
  'tpat1',
  'tpat11',
  'tpat12',
  'tpat13',
  'tpat2',
  'tpat21',
  'tpat22',
  'tpat23',
  'tpat3',
  'tpat4',
  'tpat5'
];
const alevel_subjects = [
  'a_lv_61',
  'a_lv_62',
  'a_lv_63',
  'a_lv_64',
  'a_lv_65',
  'a_lv_66',
  'a_lv_70',
  'a_lv_81',
  'a_lv_82',
  'a_lv_83',
  'a_lv_84',
  'a_lv_85',
  'a_lv_86',
  'a_lv_87',
  'a_lv_88',
  'a_lv_89',
];
const vnet_subjects = [
  'vnet_51',
  'vnet_511',
  'vnet_512',
  'vnet_513',
  'vnet_514'
];

// Cache key for S3 JSON fetches: same value within a 5-minute window so the
// browser cache can hit (S3 sends no Cache-Control), fresh URL after that.
export const s3CacheKey = () => Math.floor(Date.now() / 300000).toString(16);

// แทน url.format({ query }) ของ node (webpack 5 ไม่มี polyfill แล้ว) —
// omit ค่า nil กันได้ '?key=undefined' ซึ่ง url.format เดิมไม่ผลิต
const formatQuery = (query) => {
  const clean = _.omitBy(query || {}, _.isNil);
  return Object.keys(clean).length ? `?${new URLSearchParams(clean)}` : '';
};

export const callAPI = async ({ fullURL, url, method, query, body, genPdf, formData, onUploadProgress, accessToken, sessionToken }) => {
  if (formData) {
    const response = await axios({
      method: method || 'POST',
      url: `${genPdf ? config.genPdfApiHost : config.apiHost}${url}`,
      data: formData,
      headers: _.omitBy(
        {
          'Content-Type': formData ? null : 'application/json; charset=utf-8',
          accessToken: accessToken || Cookies.get('accessToken'),
          sessionToken: sessionToken || Cookies.get('sessionToken'),
          [SIMULATED_NOW_HEADER]: getSimulatedNow(),
        },
        _.isNil
      ),
      onUploadProgress,
    }).catch((error) => {
      // shape เดียวกับ fetch branch (statusCode/json/message) เพื่อให้ userErrorMessage ใช้ได้ทุก endpoint
      const json = _.get(error, 'response.data');
      return Promise.reject({
        statusCode: _.get(error, 'response.status'),
        json,
        message: _.get(json, 'error.message') || _.get(json, 'errors[0].msg') || _.get(json, 'message'),
      });
    });

    return Promise.resolve(response.data);
  } else {
    const response = await fetch(
      // eslint-disable-line
      `${fullURL || `${genPdf ? config.genPdfApiHost : config.apiHost}${encodeURI(url)}`}${formatQuery(query)}`,
      _.omitBy(
        {
          method: method || (body ? 'POST' : 'GET'),
          headers: _.omitBy(
            {
              'Content-Type': 'application/json; charset=utf-8',
              accessToken: accessToken || Cookies.get('accessToken'),
              sessionToken: sessionToken || Cookies.get('sessionToken'),
              // เวลาจำลอง (staging ?now=) — เฉพาะ API/api-pdf ของเรา ไม่แนบไป fullURL ภายนอก
              [SIMULATED_NOW_HEADER]: fullURL ? null : getSimulatedNow(),
            },
            _.isNil
          ),
          body: body ? JSON.stringify(body) : undefined,
        },
        _.isNil
      )
    );
    if (response.status === 200) {
      const json = await response.json();
      return Promise.resolve(json);
    } else if (response.status === 401) {
      // เก็บ body ไว้ (ถ้าเป็น JSON) — errorCode 'unauthorized' = token/session หมดอายุ ต่างจาก OTP ผิด (401 เปล่า)
      const json = await response.json().catch(() => null);
      return Promise.reject({ statusCode: response.status, json, message: 'Unauthorized' }); // eslint-disable-line
    } else {
      // body อาจไม่ใช่ JSON (เช่น sendStatus(404) = "Not Found" text/plain) — เดิม .json() โยน SyntaxError
      // ทำให้ statusCode หาย ผู้เรียกแยก 404/400 ไม่ได้ (view-folio โชว์ "ระบบขัดข้อง" แทน "ยังไม่มี PDF")
      const json = await response.json().catch(() => null);
      return Promise.reject({ statusCode: response.status, json, message: _.get(json, 'error.message') || _.get(json, 'errors[0].msg') || _.get(json, 'message') }); // eslint-disable-line
    }
  }
};

// เปิด PDF ใน tab ใหม่โดยไม่พา tab เดิมออกจากหน้า — เรียกแบบ sync ใน click handler
// (ยังอยู่ใน user gesture → Safari ไม่ block popup) แล้วค่อย navigate(pdf) หลัง await เสร็จ
// ห้ามพา tab เดิมไป pdf ด้วย location.href: กด back กลับมา browser คืนหน้าจาก bfcache
// URL ที่ presign ไว้ (เช่นรูปในแฟ้ม) เลย TTL กลายเป็น 403 — UA check includes('Safari')
// อันเก่าก็ match Chrome/Edge ด้วย ทำให้เกือบทุก browser โดน location.href
export const openPdfTab = (message = 'กำลังเปิดไฟล์ PDF กรุณารอสักครู่…') => {
  // 'about:blank' ตรงๆ ไม่ใช่ '' — iOS Safari เข้มกับ URL ว่างกว่า และคืน null ได้แม้อยู่ใน gesture
  const win = window.open('about:blank', '_blank');
  if (win) {
    // สร้าง node ผ่าน win.document เท่านั้น — node จาก document แม่ appendChild ข้าม doc ไม่ได้ทุก browser
    const doc = win.document;
    doc.title = message;
    const style = doc.createElement('style');
    style.textContent = '@keyframes pdftab-spin { to { transform: rotate(360deg); } }';
    doc.head.appendChild(style);
    const spinner = doc.createElement('div');
    Object.assign(spinner.style, {
      width: '42px', height: '42px', borderRadius: '50%',
      border: '4px solid #ddd', borderTopColor: '#555',
      animation: 'pdftab-spin 0.9s linear infinite',
    });
    const text = doc.createElement('div');
    text.textContent = message;
    const body = doc.body;
    body.textContent = '';
    body.appendChild(spinner);
    body.appendChild(text);
    Object.assign(body.style, {
      margin: '0', minHeight: '100vh',
      display: 'flex', flexDirection: 'column', gap: '20px',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'sans-serif', fontSize: '20px', color: '#555', textAlign: 'center',
    });
  }
  return {
    navigate: (pdf) => {
      // popup โดน block (iOS Safari) หรือผู้ใช้ปิด tab spinner ไปแล้วระหว่างรอ (มือถือสลับ tab) → แถบลิงก์แทน ห้าม replace tab เดิม
      if (win && !win.closed) win.location = pdf;
      else showPdfLinkToast(pdf);
    },
    close: () => { if (win) win.close(); },   // เรียกใน catch — อย่าทิ้ง tab เปล่าค้าง
  };
};

// ทางหนีเมื่อ popup โดน block: ห้าม location.href พา tab เดิมไป PDF เด็ดขาด — บน iOS Safari
// กด back จากไฟล์แล้ว SPA reload ทั้งแอป (session หาย → เด้ง login) จึงโชว์แถบลิงก์ให้แตะแทน
// การแตะเป็น user gesture สดๆ → target="_blank" ผ่าน popup blocker เสมอ
// อายุแถบ 55s สั้นกว่า presigned URL (60s) — ไม่ทิ้งลิงก์ที่แตะแล้ว 403 ค้างไว้
let _pdfLinkTimer;
export const showPdfLinkToast = (pdf) => {
  const prev = document.getElementById('app-pdf-link-toast');
  if (prev) prev.remove();
  const el = document.createElement('a');
  el.id = 'app-pdf-link-toast';
  el.href = pdf;
  el.target = '_blank';
  el.rel = 'noopener';
  el.textContent = 'ไฟล์ PDF พร้อมแล้ว — แตะที่นี่เพื่อเปิด';
  Object.assign(el.style, {
    position: 'fixed', left: '50%', top: '20px', transform: 'translateX(-50%)',
    zIndex: 9999, maxWidth: '92vw', width: 'fit-content',
    background: '#2E6CB2', color: '#fff', textDecoration: 'none',
    fontSize: '14px', fontWeight: '600', lineHeight: '1.5',
    padding: '11px 18px', borderRadius: '999px',
    boxShadow: '0 6px 24px rgba(0, 0, 0, .28)',
  });
  el.addEventListener('click', () => el.remove());
  document.body.appendChild(el);
  clearTimeout(_pdfLinkTimer);
  _pdfLinkTimer = setTimeout(() => el.remove(), 55000);
};

// 400 = ข้อความ business ที่ backend เขียนเป็นไทยให้ user แล้ว / อื่นๆ (500, network) = ดิบ ห้ามโชว์
export const userErrorMessage = (error, fallback = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง') => {
  // 429 (rate limit ต่อคนต่อนาที ฝั่ง tcas65/api-pdf) ส่ง errors[].msg แบบเดียวกับ 400 — ต้องโชว์ให้เด็กรู้ว่า "รอสักครู่" ไม่ใช่ "ระบบพัง"
  // 409 = มีรายการเดียวกันกำลังทำงาน/ข้อมูลเปลี่ยน, 503 = ระบบไม่พร้อมชั่วคราว — ข้อความจาก server อ่านรู้เรื่อง
  if (!error || ![400, 409, 429, 503].includes(error.statusCode)) return fallback;
  const msgs = (_.get(error, 'json.errors') || []).map(({ msg }) => msg).filter(Boolean);
  return msgs.join(' ') || error.message || fallback;
};

// pending tickets ใน redux เคยโหลดครั้งเดียวตอน Profile mount — หลังยื่นคำขอ หรือกลับเข้า applicant-info
// (เช่นจาก Folio Builder ที่อยู่ใต้ layout เดียวกัน) ต้องดึงใหม่ ไม่งั้นป้าย "(รอตรวจสอบ)" หาย ฟอร์มโชว์ค่าเก่า
// เด็กคิดว่าคำขอหายแล้วยื่นซ้ำ → ใบเดิมถูกยกเลิก (เคส 2026-09-11) · ดึงไม่สำเร็จ = คงค่าเดิม ไม่โยน error
export const refreshPendingTickets = async (dispatch) => {
  try {
    const { tickets } = await callAPI({ url: '/applicants/me/pending-tickets' });
    dispatch({ type: 'UPDATE_PENDING_TICKETS', tickets });
    return tickets;
  } catch (error) {
    console.error(new Error(`Error while trying to fetch pending tickets: ${error.message}`));
    return null;
  }
};

// clipboard — navigator.clipboard มีเฉพาะ secure context (บาง in-app browser ไม่มี) จึงต้องมี fallback execCommand
export const copyToClipboard = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    if (!document.execCommand('copy')) throw new Error('execCommand copy failed');
  } finally {
    document.body.removeChild(el);
  }
};

// app toast — ไม่มี toast lib ในแอป: pill ลอยกลางจอแบบเดียวกับ .folio-toast (แดง = error, เขียว = สำเร็จ)
// และเป็น DOM ล้วน เพื่อให้เรียกได้จากทุก catch โดยไม่ต้องผูก React state/context
// ข้อความยาว (เช่น รายชื่อไฟล์) → สลับ pill เป็นการ์ด: จำกัดสูง เลื่อนอ่านได้ อยู่นานพออ่าน/แคปจอ
// กดที่ toast เพื่อปิดได้ทุกแบบ · ลากเลือกข้อความไว้ copy ได้ (ปล่อยเมาส์ตอนมี selection ไม่นับเป็นกดปิด)
let _errorToastTimer;
const LONG_TOAST_CHARS = 120;   // เกินนี้ pill บรรทัดเดียวจะบวมเป็นก้อนกลมเต็มจอ → ใช้การ์ด
const hideErrorToast = (el) => {
  el.style.opacity = '0';
  el.style.pointerEvents = 'none';   // โปร่งใสแล้วต้องเลิกบังคลิก — element ค้างบน DOM แบบ position:fixed
};
const TOAST_BG = { error: '#c0392b', success: '#1e7a48' };
export const showErrorToast = (message) => _showToast(message, 'error');
// toast สำเร็จ — งานที่ผลลัพธ์ไม่เห็นบนจอ (คัดลอกลิงก์) ต้องมีคำยืนยัน ไม่งั้นเด็กกดซ้ำรัวเพราะคิดว่าไม่ทำงาน
export const showSuccessToast = (message) => _showToast(message, 'success');
const _showToast = (message, variant = 'error') => {
  let el = document.getElementById('app-error-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'app-error-toast';
    el.title = 'กดเพื่อปิด';
    Object.assign(el.style, {
      position: 'fixed', left: '50%', top: '20px', transform: 'translateX(-50%)',
      zIndex: 9999, width: 'fit-content',
      color: '#fff',
      fontSize: '14px', fontWeight: '600', lineHeight: '1.5',
      padding: '11px 18px',
      boxShadow: '0 6px 24px rgba(0, 0, 0, .28)',
      transition: 'opacity .22s ease-out', opacity: '0',
      cursor: 'pointer', userSelect: 'text',
    });
    el.addEventListener('click', () => {
      if (window.getSelection && String(window.getSelection())) return;   // กำลัง copy อยู่
      clearTimeout(_errorToastTimer);
      hideErrorToast(el);
    });
    document.body.appendChild(el);
  }
  el.setAttribute('role', variant === 'error' ? 'alert' : 'status');
  el.style.background = TOAST_BG[variant] || TOAST_BG.error;
  const long = message.length > LONG_TOAST_CHARS;
  Object.assign(el.style, long
    ? { borderRadius: '14px', textAlign: 'left', maxWidth: 'min(92vw, 560px)', maxHeight: '40vh', overflowY: 'auto' }
    : { borderRadius: '999px', textAlign: 'center', maxWidth: '92vw', maxHeight: '', overflowY: '' });
  el.textContent = message;
  el.scrollTop = 0;
  el.style.pointerEvents = 'auto';
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  clearTimeout(_errorToastTimer);
  _errorToastTimer = setTimeout(() => hideErrorToast(el), long ? 30000 : 5000);
};

export const isCitizenId = (id = '') => {
  if (!id.toLowerCase().match(/^[0-9]{13}$/i)) return false;
  const sum = Array(12)
    .fill()
    .reduce((sum, _, index) => sum + id[index] * (13 - index), 0);
  return parseInt(id.slice(-1), 10) === (11 - (sum % 11)) % 10;
};
export const isGNumber = (id = '') => {
  return id.toLowerCase().match(/^g[0-9]{12}$/i);
};
const isPassportNumber = (id = '') => {
  return id.toLowerCase().match(/^[a-z0-9]{7,13}$/);
};

export const getCitizenIdType = (id) => {
  if (isCitizenId(id)) return 'citizen_id';
  if (isGNumber(id)) return 'gnumber';
  if (!isNaN(id) && id.length >= 10) return null;
  if (isPassportNumber(id)) return 'passport';

  return null;
};

export const getCitizenTypeLabel = (citizenIdType) => {
  return {
    citizen_id: 'เลขประจำตัวประชาชน',
    gnumber: 'Gnumber',
    passport: 'Passport Number',
  }[citizenIdType];
};

// ห้าม export ตัวนี้ตรง ๆ — ทุก uploader ต้องผ่าน validate ใน pickCompressedImage / compressUploadImage
const COMPRESS_TIMEOUT_MS = 20000;
const compressImage = (file) => {
  if (!file) return Promise.resolve(file);
  return new Promise((resolve, reject) => {
    // Compressor ค้าง (canvas ใหญ่บน iOS เก่า/หน่วยความจำไม่พอ) → เดิม promise ไม่จบ overlay "กำลังย่อรูป" ค้างตลอด
    const timer = setTimeout(() => reject(Object.assign(new Error('compress timeout'), { code: 'timeout' })), COMPRESS_TIMEOUT_MS);
    new Compressor(file, {
      quality: 0.8,
      maxWidth: 1920,
      maxHeight: 1920,
      resize: 'cover',
      // auto-convert ทุกสกุลเป็น .jpg — สกุลอื่น (.webp ฯลฯ) เคยหลุดขึ้นบัตรสอบแล้วระบบแสดงผลไม่ได้
      mimeType: 'image/jpeg',
      // พื้นหลังโปร่งใส (png/webp) ต้องถมขาวก่อนแปลงเป็น jpeg ไม่งั้นออกมาดำ
      beforeDraw(context, canvas) {
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
      },
      success(result) {
        clearTimeout(timer);
        resolve(result);
      },
      error(err) {
        clearTimeout(timer);
        reject(err);
      },
    });
  });
};

// cap ไว้กัน abuse เท่านั้น — รูปจริงจากกล้องต้องผ่าน (compress ย่อทีหลังอยู่แล้ว)
const UPLOAD_IMAGE_MAX_BYTES = 20 * 1024 * 1024;

const UPLOAD_IMAGE_ERROR_MESSAGES = {
  type: 'กรุณาเลือกไฟล์รูปภาพ',
  size: 'ไฟล์รูปภาพต้องมีขนาดไม่เกิน 20 MB',
  unreadable: 'ไม่สามารถอ่านไฟล์รูปภาพได้ กรุณาเลือกไฟล์ใหม่',
  heic: 'ไฟล์ HEIC/HEIF ใช้ไม่ได้ในเบราว์เซอร์นี้ กรุณาแปลงเป็น JPG หรือ PNG ก่อน (iPhone: ตั้งค่ากล้อง → รูปแบบ → เข้ากันได้มากที่สุด)',
};

// รับทุกสกุลรูป — Compressor แปลงเป็น jpeg ให้เอง สกุลที่ browser decode ไม่ได้ (เช่น heic
// บน Chrome) จะหลุดไปทาง error 'unreadable' ตอน compress
const isHeicFile = (file) => /hei[cf]/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');
const validateUploadImage = (file) => {
  // HEIC ไม่บล็อกล่วงหน้า — Safari ≥17 decode ได้ (Compressor แปลงเป็น JPEG ผ่าน) · ที่ decode ไม่ได้ (Chrome/Android) จะตกมาที่ catch ด้านล่างแล้วบอกสาเหตุ
  if (!file.type.startsWith('image/') && !isHeicFile(file)) return 'type';
  if (file.size > UPLOAD_IMAGE_MAX_BYTES) return 'size';
  return null;
};

// สำหรับ flow ที่ถือ File เอง (เช่น face check, ตัวเลือกรูปของ FolioBuilder ที่ไม่ได้ใช้
// pickCompressedImage เพราะ input ตัวเดียว reuse ทุกกรอบ) — คืน { file } หรือ { error: code, message }
export const compressUploadImage = async (file) => {
  if (!file) return { file: null };
  const error = validateUploadImage(file);
  if (error) return { error, message: UPLOAD_IMAGE_ERROR_MESSAGES[error] };
  try {
    return { file: await compressImage(file) };
  } catch (e) {
    // HEIC จาก "ไฟล์" บน Chrome/Android decode ไม่ได้ (Photos picker บน iOS แปลงเป็น JPEG ให้เอง) — บอกสาเหตุตรง ๆ แทน "อ่านไฟล์ไม่ได้"
    const error = isHeicFile(file) ? 'heic' : 'unreadable';
    return { error, message: UPLOAD_IMAGE_ERROR_MESSAGES[error] };
  }
};

// G49-1: จำรอบเลือกต่อ input เพื่อไม่ให้รูปเก่าที่ย่อช้ากว่าทับรูปที่เลือกใหม่
const imageSelections = new WeakMap();
// accept="image/*" เป็นแค่ hint ของ file picker — ต้อง validate type/size ที่นี่อีกชั้น
export const pickCompressedImage = async (e) => {
  const input = e.target;
  const picked = input.files[0];
  if (!picked) return null;
  const selection = {};
  imageSelections.set(input, selection);
  // เคลียร์ value ตอน "คลิกครั้งถัดไป" (ก่อน dialog เปิด) แทนเคลียร์ทันทีหลังเลือก —
  // ให้ input โชว์ชื่อไฟล์ที่เลือกไว้ (ไม่ค้าง "No file chosen") และยังเลือกไฟล์เดิมซ้ำแล้ว onChange ยิง
  input.onclick = () => {
    input.value = '';
  };
  const { file, error } = await compressUploadImage(picked);
  if (imageSelections.get(input) !== selection) return null;
  imageSelections.delete(input);
  if (error) {
    input.value = '';
    showErrorToast(UPLOAD_IMAGE_ERROR_MESSAGES[error]);
    return null;
  }
  return file;
};

export const getProvinceName = (provinceList, provinceId) =>
  _.get(
    _.find(provinceList, (province) => province.provinceId === provinceId),
    'provinceName'
  );

export const getDistrictName = (districtList, districtId) =>
  _.get(
    _.find(districtList, (district) => district.districtId === districtId),
    'districtName'
  );

export const getSubDistrictName = (subDistrictList, subDistrictId) =>
  _.get(
    _.find(subDistrictList, (subDistrict) => subDistrict.subDistrictId === subDistrictId),
    'subDistrictName'
  );

export const getNewestTicket = (pendingTickets, category) => {
  const categories = Array.isArray(category) ? category : [category];
  const ticket = _.last(pendingTickets.filter((_ticket) => categories.includes(_ticket.category)));
  return {
    ticket,
    valueOf: (fieldName) => _.get(ticket, `requested_data.${fieldName}.value`),
  };
};

export const handleThaiUniqueString = (stringToReplace = '') => {
  let result = stringToReplace.replace(/เเ/g, 'แ');
  result = result.replace(/ํา/g, 'ำ').replace(/าํ/g, 'ำ');
  result = result.replace(/์ิ/g, 'ิ์');
  result = result.replace(/์ุ/g, 'ุ์');
  return result;
};



export const wordsWithStyles = (keywords = [], string = '') => {
  // ช่วง [start,end) ของคำค้นแต่ละคำ (ตำแหน่งแรกที่พบ) → รวมช่วงที่ซ้อน/ครอบกัน → หั่นเป็น segment หนา/ไม่หนา
  // (เดิมรวมช่วงผิดเมื่อคำหนึ่งอยู่ในอีกคำ เช่น ["เกษตร","มหาวิทยาลัยเกษตรศาสตร์"] → ข้อความซ้ำ/หายบางส่วน)
  const text = `${string == null ? '' : string}`;
  const ranges = (keywords || [])
    .filter(Boolean)
    .map((word) => { const i = text.indexOf(word); return i === -1 ? null : [i, i + word.length]; })
    .filter(Boolean)
    .sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged = [];
  ranges.forEach(([a, b]) => {
    const last = merged[merged.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  });
  if (!merged.length) return [{ word: text, bold: false }];
  const out = [];
  let cursor = 0;
  merged.forEach(([a, b]) => {
    if (a > cursor) out.push({ word: text.slice(cursor, a), bold: false });
    out.push({ word: text.slice(a, b), bold: true });
    cursor = b;
  });
  if (cursor < text.length) out.push({ word: text.slice(cursor), bold: false });
  return out;
};

export const renderBoldString = ({ word, bold }, index) => (bold ? <strong key={index}>{word}</strong> : <span key={index}>{word}</span>);

export const notApplicable = (student, programProject, { studentStatus = {}, favorites = [], nextPriority = 1 }) => {
  let errors = [];
  if (!student) return [];
  if (!programProject) return [];
  if (!isValidGpax(student.gpax6_score) || !canCalculateGpax(student, programProject)) {
    errors.push({ key: 'invalid_gpax', label: 'กรุณาแก้ไข GPAX ให้เป็นตัวเลขระหว่าง 0–4 ก่อนสมัคร' });
  }

  // Validate receive_student_number greater than 0
  if (!programProject.receive_student_number) {
    errors.push({ key: 'disabled', label: 'งดรับผู้สมัคร' });
  }

  switch (studentStatus.code) {
    case 10:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '10') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 11:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '12') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 12:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '13') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 16:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '10') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 17:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '12') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 18:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '13') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    case 19:
      if (programProject.university_type_id !== '4' && programProject.group_field_id === '14') {
        errors.push({ key: studentStatus.code, label: studentStatus.message.split(' --> ')[0] });
      }
      break;
    default:
      break;
  }

  // Validate gender_male_number
  if (programProject.gender_male_number === programProject.receive_student_number && (!student.title || ['นาง', 'นางสาว', 'น.ส.', 'Miss'].includes(student.title))) {
    errors.push({ key: 'gender_male_number', label: 'รับเฉพาะเพศ', expected: 'ชาย', value: !student.title ? 'ไม่ระบุ' : 'หญิง' });
  }

  // Validate gender_female_number
  if (programProject.gender_female_number === programProject.receive_student_number && (!student.title || ['นาย', 'สามเณร', 'Mr', 'นชท.'].includes(student.title))) {
    errors.push({ key: 'gender_female_number', label: 'รับเฉพาะเพศ', expected: 'หญิง', value: !student.title ? 'ไม่ระบุ' : 'ชาย' });
  }

  // Validate no program_code
  if (!student.program_code) {
    errors.push({ key: 'no_program_code', label: 'ไม่พบข้อมูลหลักสูตรของผู้สมัคร' });
  }

  // Validate only_formal
  if (programProject.only_formal === 2 && student.program_code === '1') {
    errors.push({ key: 'only_formal', label: 'ไม่รับผู้สมัครจากหลักสูตร', expected: 'แกนกลาง (สามัญ)', value: 'แกนกลาง (สามัญ)' });
  }

  // Validate only_international
  if (programProject.only_international === 2 && student.program_code === '2') {
    errors.push({ key: 'only_international', label: 'ไม่รับผู้สมัครจากหลักสูตร', expected: 'นานาชาติ', value: 'หลักสูตรนานาชาติ' });
  }

  // Validate only_vocational
  if (programProject.only_vocational === 2 && student.program_code === '3') {
    errors.push({ key: 'only_vocational', label: 'ไม่รับผู้สมัครจากหลักสูตร', expected: 'อาชีวะ', value: 'หลักสูตรอาชีวะ' });
  }

  // Validate only_non_formal
  if (programProject.only_non_formal === 2 && student.program_code === '4') {
    errors.push({ key: 'only_non_formal', label: 'ไม่รับผู้สมัครจากหลักสูตร', expected: 'ตามอัธยาศัย (กศน.)', value: 'หลักสูตรตามอัธยาศัย (กศน.)' });
  }

  // Validate only_ged
  if (programProject.only_ged === 2 && ['6'].includes(student.program_code)) {
    errors.push({ key: 'only_ged', label: 'ไม่รับผู้สมัครจากหลักสูตร', expected: 'สอบเทียบ GED', value: programCodes[student.program_code] });
  }

  // Validate min priority
  if (programProject.min_priority) {
    const current = _.find(favorites, ({ program_project }) => program_project === programProject._id) || {};
    if ((current.priority && current.priority > programProject.min_priority) || (!current.priority && nextPriority > programProject.min_priority)) {
      errors.push({ key: 'min_priority', label: 'ลำดับขั้นต่ำในการเลือกสาขา', expected: programProject.min_priority, value: current.priority || `มากกว่า ${programProject.min_priority}` })
    }
  }

  // Validate birth date for age rules — วันเกิดว่าง/ผิดรูปแบบ moment ได้ NaN แล้วเงื่อนไขอายุผ่านเงียบ ๆ
  if ((programProject.min_age || programProject.max_age) && !moment(student.birth_date, 'YYYY-MM-DD', true).isValid()) {
    errors.push({ key: 'invalid_birth_date', label: 'กรุณาระบุวันเกิดในข้อมูลพื้นฐานให้ถูกต้อง' });
  }

  // Validate min age
  if (programProject.min_age) {
    const birth_date = moment(student.birth_date, 'YYYY-MM-DD');
    if (programProject.min_age_date) {
      if (moment(programProject.min_age_date).diff(birth_date, 'year') < programProject.min_age) {
        errors.push({
          key: 'min_age_date',
          label: `ในวันที่ ${moment(programProject.min_age_date).format('DD/MM/YYYY')} ${fields.min_age.label}`,
          expected: `${programProject.min_age} ปี`,
          value: `${moment(programProject.min_age_date).diff(birth_date, 'year')} ปี`
        });
      }
    } else {
      if (moment().year() - birth_date.year() < programProject.min_age) {
        errors.push({
          key: 'min_age',
          label: fields.min_age.label,
          expected: `${programProject.min_age} ปี`,
          value: `${moment().year() - birth_date.year()} ปี`
        });
      }
    }
  }

  // Validate max age
  if (programProject.max_age) {
    const birth_date = moment(student.birth_date, 'YYYY-MM-DD');
    if (programProject.max_age_date) {
      if (moment(programProject.max_age_date).diff(birth_date, 'year') > programProject.max_age) {
        errors.push({
          key: 'max_age_date',
          label: `ในวันที่ ${moment(programProject.max_age_date).format('DD/MM/YYYY')} ${fields.max_age.label}`,
          expected: `${programProject.max_age} ปี`,
          value: `${moment(programProject.max_age_date).diff(birth_date, 'year')} ปี`
        });
      }
    } else {
      if (moment().year() - birth_date.year() > programProject.max_age) {
        errors.push({
          key: 'max_age',
          label: fields.max_age.label,
          expected: `${programProject.max_age} ปี`,
          value: `${moment().year() - birth_date.year()} ปี`
        });
      }
    }
  }

  const isMale = ['นาย', 'สามเณร'].includes(student.title);
  const bmi = !(student.weight && student.height) ? 0 : (student.weight / (student.height * student.height / 10000));

  // Validate min height of male student
  if (programProject.min_height_male && isMale && (!student.height || student.height < programProject.min_height_male)) {
    errors.push({
      key: 'min_height_male',
      label: fields.min_height_male.label,
      expected: `${programProject.min_height_male} ซม.`,
      value: !student.height ? 'ไม่ระบุ' : `${student.height} ซม.`
    });
  };

  // Validate min height of female student
  if (programProject.min_height_female && !isMale && (!student.height || student.height < programProject.min_height_female)) {
    errors.push({
      key: 'min_height_female',
      label: fields.min_height_female.label,
      expected: `${programProject.min_height_female} ซม.`,
      value: !student.height ? 'ไม่ระบุ' : `${student.height} ซม.`
    });
  }

  // Validate min weight of male student
  if (programProject.min_weight_male && isMale && (!student.weight || student.weight < programProject.min_weight_male)) {
    errors.push({
      key: 'min_weight_male',
      label: fields.min_weight_male.label,
      expected: `${programProject.min_weight_male} กก.`,
      value: !student.weight ? 'ไม่ระบุ' : `${student.weight} กก.`
    });
  }

  // Validate min weight of female student
  if (programProject.min_weight_female && !isMale && (!student.weight || student.weight < programProject.min_weight_female)) {
    errors.push({
      key: 'min_weight_female',
      label: fields.min_weight_female.label,
      expected: `${programProject.min_weight_female} กก.`,
      value: !student.weight ? 'ไม่ระบุ' : `${student.weight} กก.`
    });
  }

  // Validate max weight of male student
  if (programProject.max_weight_male && isMale && (!student.weight || student.weight > programProject.max_weight_male)) {
    errors.push({
      key: 'max_weight_male',
      label: fields.max_weight_male.label,
      expected: `${programProject.max_weight_male} กก.`,
      value: !student.weight ? 'ไม่ระบุ' : `${student.weight} กก.`
    });
  }

  // Validate max weight of female student
  if (programProject.max_weight_female && !isMale && (!student.weight || student.weight > programProject.max_weight_female)) {
    errors.push({
      key: 'max_weight_female',
      label: fields.max_weight_female.label,
      expected: `${programProject.max_weight_female} กก.`,
      value: !student.weight ? 'ไม่ระบุ' : `${student.weight} กก.`
    });
  }

  // Validate min bmi of male student
  if (programProject.min_bmi_male && isMale && (!bmi || bmi < programProject.min_bmi_male)) {
    errors.push({
      key: 'min_bmi_male',
      label: fields.min_bmi_male.label,
      expected: programProject.min_bmi_male,
      value: !bmi ? 'ไม่ระบุ' : bmi.toFixed(2)
    });
  }

  // Validate min bmi of female student
  if (programProject.min_bmi_female && !isMale && (!bmi || bmi < programProject.min_bmi_female)) {
    errors.push({
      key: 'min_bmi_female',
      label: fields.min_bmi_female.label,
      expected: programProject.min_bmi_female,
      value: !bmi ? 'ไม่ระบุ' : bmi.toFixed(2)
    });
  }

  // Validate max bmi of male student
  if (programProject.max_bmi_male && isMale && (!bmi || bmi > programProject.max_bmi_male)) {
    errors.push({
      key: 'max_bmi_male',
      label: fields.max_bmi_male.label,
      expected: programProject.max_bmi_male,
      value: !bmi ? 'ไม่ระบุ' : bmi.toFixed(2)
    });
  }

  // Validate max bmi of female student
  if (programProject.max_bmi_female && !isMale && (!bmi || bmi > programProject.max_bmi_female)) {
    errors.push({
      key: 'max_bmi_female',
      label: fields.max_bmi_female.label,
      expected: programProject.max_bmi_female,
      value: !bmi ? 'ไม่ระบุ' : bmi.toFixed(2)
    });
  }

  // Validate min gpax
  if (programProject.min_gpax && _ensureScore(student.gpax6_score) < programProject.min_gpax) {
    errors.push({
      key: 'min_gpax',
      label: fields.min_gpax.label,
      expected: programProject.min_gpax,
      value: !student.gpax6_score ? 'ไม่ระบุ' : student.gpax6_score
    });
  }

  // Validate GPAs and their credits
  [21, 22, 23, 24, 25, 26, 27, 28, 29].forEach(gpa_code => {
    if (programProject[`min_credit_gpa${gpa_code}`] && _ensureScore(student[`credit_gpa${gpa_code}`]) < programProject[`min_credit_gpa${gpa_code}`]) {
      errors.push({
        key: `min_credit_gpa${gpa_code}`,
        label: fields[`min_credit_gpa${gpa_code}`].label,
        expected: programProject[`min_credit_gpa${gpa_code}`],
        value: _ensureScore(student[`credit_gpa${gpa_code}`])
      });
    }
    if (programProject[`min_gpa${gpa_code}`] && _ensureScore(student[`gpa${gpa_code}`]) < programProject[`min_gpa${gpa_code}`]) {
      errors.push({
        key: `min_gpa${gpa_code}`,
        label: fields[`min_gpa${gpa_code}`].label,
        expected: programProject[`min_gpa${gpa_code}`],
        value: _ensureScore(student[`gpa${gpa_code}`])
      });
    }
  });

  // Validate min_credit_gpa22_23
  if (programProject.min_credit_gpa22_23 && (_ensureScore(student.credit_gpa22) + _ensureScore(student.credit_gpa23)) < programProject.min_credit_gpa22_23) {
    errors.push({
      key: 'min_credit_gpa22_23',
      label: fields.min_credit_gpa22_23.label,
      expected: programProject.min_credit_gpa22_23,
      value: (_ensureScore(student.credit_gpa22) + _ensureScore(student.credit_gpa23)).toFixed(4)
    });
  }

  // Validate min_credit_gpa22_23_28
  if (programProject.min_credit_gpa22_23_28 && (_ensureScore(student.credit_gpa22) + _ensureScore(student.credit_gpa23) + _ensureScore(student.credit_gpa28)) < programProject.min_credit_gpa22_23_28) {
    errors.push({
      key: 'min_credit_gpa22_23_28',
      label: fields.min_credit_gpa22_23_28.label,
      expected: programProject.min_credit_gpa22_23_28,
      value: (_ensureScore(student.credit_gpa22) + _ensureScore(student.credit_gpa23) + _ensureScore(student.credit_gpa28)).toFixed(4)
    });
  }

  // Validate min_gpa22_23
  if (programProject.min_gpa22_23 && ((_ensureScore(student.gpa22) + _ensureScore(student.gpa23)) / 2) < programProject.min_gpa22_23) {
    errors.push({
      key: 'min_gpa22_23',
      label: fields.min_gpa22_23.label,
      expected: programProject.min_gpa22_23,
      value: ((_ensureScore(student.gpa22) + _ensureScore(student.gpa23)) / 2).toFixed(4)
    });
  }

  // Validate min_gpa22_23_28
  if (programProject.min_gpa22_23_28 && ((_ensureScore(student.gpa22) + _ensureScore(student.gpa23) + _ensureScore(student.gpa28)) / 3) < programProject.min_gpa22_23_28) {
    errors.push({
      key: 'min_gpa22_23_28',
      label: fields.min_gpa22_23_28.label,
      expected: programProject.min_gpa22_23_28,
      value: ((_ensureScore(student.gpa22) + _ensureScore(student.gpa23) + _ensureScore(student.gpa28)) / 3)
    });
  }

  // Validate grad current
  if (programProject.grad_current && student.school_year !== CURRENT_SCHOOL_YEAR) {
    errors.push({
      key: 'grad_current',
      label: 'ต้องสำเร็จการศึกษาปี',
      expected: CURRENT_SCHOOL_YEAR,
      value: student.school_year
    });
  }

  // Validate scores
  (tgattpat_subjects.concat(alevel_subjects)).forEach(subject => {
    const min_score = _.get(programProject, `score_conditions.min_${subject}`);
    if (min_score && _ensureScore(student[subject]) < min_score) {
      errors.push({
        key: `min_${subject}`,
        label: `คะแนนขั้นต่ำของ ${subjectMap[subject].label}`,
        expected: min_score,
        value: _ensureScore(student[subject]) || 0
      });
    }

    const min_tscore = _.get(programProject, `score_conditions.min_${subject}_tscore`);
    if (min_tscore && _ensureScore(student[`${subject}_tscore`]) < min_tscore) {
      errors.push({
        key: `min_${subject}_tscore`,
        label: `คะแนนขั้นต่ำของคะแนน T-Score ของ ${subjectMap[subject].label}`,
        expected: min_tscore,
        value: _ensureScore(student[`${subject}_tscore`]) || 0
      });
    }

    const min_pr = _.get(programProject, `score_conditions.min_${subject}_pr`);
    if (min_pr && _ensureScore(student[`${subject}_ps`]) < min_pr) {
      errors.push({
        key: `min_${subject}_pr`,
        label: `Percentile Rank ขั้นต่ำของ ${subjectMap[subject].label}`,
        expected: min_pr,
        value: _ensureScore(student[`${subject}_ps`]) || 0
      });
    }
  });

  [
    'toefl_ibt',
    'toefl_pbt',
    'toefl_cbt',
    'toefl_itp',
    'ielts',
    'toeic',
    'cutep',
    'tuget',
    'kept',
    'psutep',
    'kuept',
    'cmuetegs',
    'swu_set',
    'det',
    'sat',
    'cefr',
    'kmitl_tep',
    'ged_score',
    'cotmes_01',
    'cotmes_02',
    'cotmes_03',
    'mu001',
    'mu002',
    'mu003',
    'su001',
    'su002',
    'su003',
    'su004',
    'su005',
    'su006',
    'su007',
    'su008',
    'su009',
    'su010',
    'su011',
    'su012',
    'su013',
    'su014',
    'tu001',
    'tu002',
    'tu003',
    'tu004',
    'tu005',
    'tu006',
    'tu061',
    'tu062',
    'tu071',
    'tu072',
    'tu081',
    'tu082',
    'tu091',
    'tu092',
    'gsat',
    'gsat_l',
    'gsat_m',
    'mu_elt',
    'netsat_math',
    'netsat_lang_th',
    'netsat_lang_en',
    'netsat_sci',
    'netsat_phy',
    'netsat_chem',
    'netsat_bio'
  ].forEach(subject => {
    const min_score = _ensureScore(_.get(programProject, `score_conditions.min_${subject}`));
    if (min_score && (_ensureScore(student[subject]) || 0) < min_score) {
      errors.push({
        key: `min_${subject}`,
        label: `คะแนนขั้นต่ำของ ${subjectMap[subject].label}`,
        expected: min_score,
        value: _ensureScore(student[subject]) || 0
      });
    }
  });

  vnet_subjects.forEach(subject => {
    const min_score = _.get(programProject, `score_conditions.min_${subject}`);
    if (min_score && (_ensureScore(student[subject]) < min_score || student.program_code !== '3')) {
      errors.push({
        key: `min_${subject}`,
        label: `คะแนนขั้นต่ำ${subjectMap[subject].label}`,
        expected: min_score,
        value: _ensureScore(student[subject])
      });
    }
  });

  const subject_names = (_.get(programProject, 'score_conditions.subject_names') || '')
    .trim()
    .split(' ')
    .filter(value => value)
    .map(key => key.replace(/min_/g, '').replace(/_pr/g, '_ps'));
  const score_condition = `${_.get(programProject, 'score_conditions.score_condition')}`

  switch (score_condition) {
    case '1':
      const min_scores_raw = (_.get(programProject, 'score_conditions.score_minimum') || '0').trim().split(' ');
      const min_scores = min_scores_raw.map((raw, index) => {
        if (subject_names[index] === 'cefr') {
          const cefrMin = _cefrLevel(raw);
          if (cefrMin) return cefrMin;
        }
        return parseFloat(raw, 10);
      });
      const _scoreOf = (subject) => subject === 'cefr' ? _cefrLevel(student[subject]) : _ensureScore(student[subject]);
      if (!subject_names.some((subject, index) => _scoreOf(subject) >= min_scores[index])) {
        subject_names.forEach((subject, index) => {
          const _score = _scoreOf(subject);
          if (_score < min_scores[index]) {
            const minDisplay = subject === 'cefr' && _cefrLevel(min_scores_raw[index])
              ? String(min_scores_raw[index]).trim().toUpperCase()
              : min_scores[index];
            const valueDisplay = subject === 'cefr'
              ? (student[subject] ? String(student[subject]).trim().toUpperCase() : 0)
              : _score;
            errors.push({
              key: 'score_condition_1',
              label: `คะแนนขั้นต่ำของวิชาใดวิชาหนึ่งระหว่าง ${subject_names.map(subject => subjectMap[subject.replace(/_tscore/g, '').replace(/_ps/g, '')].label).join(', ').toUpperCase()}`,
              expected: `${subjectMap[subject.replace(/_tscore/g, '').replace(/_ps/g, '')].label} ไม่ต่ำกว่า ${minDisplay}`,
              value: valueDisplay
            });
          }
        });
      }
      break;
    case '2':
      const score_minimum = parseFloat(_.get(programProject, 'score_conditions.score_minimum') || '0');
      const _sum = _.sumBy(subject_names, subject => _ensureScore(student[subject]));
      if (_sum < score_minimum) {
        errors.push({
          key: 'score_condition_2',
          label: `คะแนนขั้นต่ำของผลรวมวิชา ${subject_names.map(subject => subjectMap[subject.replace(/_tscore/g, '').replace(/_ps/g, '')].label).join(', ').toUpperCase()}`,
          expected: score_minimum,
          value: _sum
        });
      }
      break;
    default:
      break;
  }

  const project_score = getProjectScore(student, programProject, nextPriority);
  if (programProject.min_total_score > project_score) {
    errors.push({
      key: 'min_total_score',
      label: 'คะแนนรวมขั้นต่ำ',
      expected: programProject.min_total_score,
      value: project_score
    });
  }

  const error_groups = _.groupBy(errors, 'key');
  return Object.keys(error_groups).map(key => {
    const messages = error_groups[key];
    if (messages.length === 1) return error_groups[key][0];
    return { key, messages }
  });
};

const max_value_map = subjectLabels.reduce((map, { key, max_value }) => ({ ...map, [key]: max_value }), {});

const roundUpFloat = (number) => {
  const floatNumber = parseFloat(number);
  if ((floatNumber % 0.0001).toFixed(6) === '0.000050') {
    return _.ceil(floatNumber, 4).toFixed(4);
  }
  return floatNumber.toFixed(4);
}


// pure ล้วน (student+project+priority → score) แต่แพง (cloneDeep + reduce ~90 วิชา)
// และถูกเรียกซ้ำ 2 ครั้ง/แถวใน list render ของ round 3 — cache ตาม identity ของ
// object จาก redux (เปลี่ยนค่า = object ใหม่ = cache ใหม่เอง)
const projectScoreCache = new WeakMap();
export const getProjectScore = (student, programProject, priority) => {
  if (!student || !programProject) return '0.0000';
  if (!canCalculateGpax(student, programProject)) return '—';
  let byProject = projectScoreCache.get(student);
  if (!byProject) { byProject = new WeakMap(); projectScoreCache.set(student, byProject); }
  let byPriority = byProject.get(programProject);
  if (!byPriority) { byPriority = new Map(); byProject.set(programProject, byPriority); }
  const cacheKey = String(priority);
  if (!byPriority.has(cacheKey)) byPriority.set(cacheKey, computeProjectScore(student, programProject, priority));
  return byPriority.get(cacheKey);
};

const computeProjectScore = (student, programProject, priority) => {
  const getScore = (_subject) => {
    const subjects = typeof _subject === 'string' ? [_subject] : _subject;
    if (!programProject) return '0.0000';
    if (programProject.t_score) return _.sumBy(subjects, subject => _ensureScore(student[`${subject}_tscore`])).toFixed(4) || '0.0000';
    return roundUpFloat(_.sumBy(subjects, subject => _ensureScore(student[subject]))) || '0.0000';
  };

  const tscore_subjects = (tgattpat_subjects.concat(alevel_subjects));
  const data = tscore_subjects.reduce((sc, subject) => {
    return _.merge(sc, { [subject]: getScore(subject) });
  }, _.merge(_.cloneDeep(student), {
    priority_score: 100 - 10 * (parseInt(priority, 10) - 1),
    gpax: parseFloat(student.gpax6_score),
    gpa22_23: _.mean(['gpa22', 'gpa23'].map(gpa => _ensureScore(student[gpa]))),
    gpa22_23_28: _.mean(['gpa22', 'gpa23', 'gpa28'].map(gpa => _ensureScore(student[gpa]))),
  }));

  const scores = programProject.scores || {};
  if (scores.cal_type) {
    const { cal_score } = _.maxBy((scores.cal_subject_name || '').trim().split('|').map(subject_string => {
      const subject_names = subject_string.split(' ');
      const total_scores = _.sumBy(subject_names, subject => (parseFloat(data[subject]) / max_value_map[subject] * 100) || 0);
      const max_value = 100 * subject_names.length;
      const cal_score = total_scores / max_value * scores.cal_score_sum;

      return {
        subject_names,
        total_scores,
        max_value,
        cal_score
      };
    }), 'cal_score');

    const total_score = parseFloat(Object.keys(scores).reduce((sum, key) => {
      return sum + (key.startsWith('cal_') ? 0 : ((parseFloat(data[key]) || 0) * scores[key] / ((programProject.t_score && tscore_subjects.includes(key)) ? 100 : max_value_map[key])))
    }, cal_score));

    return roundUpFloat(total_score);
  } else {
    const total_score = parseFloat(Object.keys(scores).reduce((sum, key) => {
      return sum + (key.startsWith('cal_') ? 0 : ((parseFloat(data[key]) || 0) * scores[key] / ((programProject.t_score && tscore_subjects.includes(key)) ? 100 : max_value_map[key])))
    }, 0));

    return roundUpFloat(total_score);
  }
};

export const getFullScore = (programProject = {}) => {
  // const scores = programProject.scores || {};
  // if (scores.tpat1) return (100 - (scores.tpat1 * 2 /3)).toFixed(2);
  // if (scores.tpat11) return 100 - scores.tpat11;
  return 100;
}

export const getRoundState = ({ roundType, timestamp, query }) => {
  const round = enabled[`round${roundType.split('_')[0]}`];
  // ใช้เฉพาะ key ที่ค่าเป็น ISO date จริง (ข้าม config อื่นเช่น cancel_target) แล้วเรียงตามเวลาแบบตัวเลข
  const dateKeys = Object.keys(round)
    .filter((key) => moment(round[key], moment.ISO_8601, true).isValid())
    .sort((a, b) => moment(round[a]).valueOf() - moment(round[b]).valueOf());
  const states = ['not-open', 'default', 'expired', 'cancel', 'cancel-expired'];
  // นับ boundary ที่ผ่านมาแล้ว; clamp กัน index ทะลุ (round3 มี 7 boundary แต่ states มี 5 → เดิม flow = undefined)
  const passed = dateKeys.reduce((count, key) => (moment(timestamp).isAfter(round[key]) ? count + 1 : count), 0);
  const index = Math.min(passed, states.length - 1);

  // override ?flow= ใช้ได้เฉพาะ dev/e2e/staging — production จริงถูก strip ทิ้ง (env inline ตอน build)
  // REACT_APP_E2E: e2e โหมด E2E_PROD เสิร์ฟ production build จึงต้องเปิด gate นี้ด้วย
  const flowOverrideEnabled = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_STAGING === 'true' || process.env.REACT_APP_E2E === 'true';
  const flowOverride = flowOverrideEnabled && query ? query.get('flow') : null;

  return {
    flow: flowOverride || states[index],
    period: `${moment(round[_.first(dateKeys)]).format('D MMM')} - ${moment(round[_.last(dateKeys)]).format('D MMM')} ${moment(round[_.last(dateKeys)]).add(543, 'years').format('YY')}`
  }
}

export const getRoundDescription = ({ roundType, timestamp, alreadyApplied, offers = [], replies = [] }) => {
  const { flow, period } = getRoundState({ roundType, timestamp });
  if (flow === 'not-open') return period;
  if (alreadyApplied) return 'ไม่มีสิทธิ์ในรอบนี้';
  if (!replies.length && flow === 'default') return offers.length ? `ผ่านการคัดเลือก ${offers.length} สาขาวิชา` : 'ท่านไม่ผ่านการคัดเลือกในรอบนี้';
  if (_.get(_.last(replies), 'is_canceled')) return 'สละสิทธิ์ในรอบนี้';
  if (_.get(_.last(replies), 'enrollment_offer')) return 'ยืนยันสิทธิ์เรียบร้อยแล้ว';
  return 'ไม่ใช้สิทธิ์ในรอบนี้';
}
// TCASFolio ปิดรับสมัคร → ห้ามแก้ (#9) — วันปิดเป็นรายหลักสูตร จาก courses_round1.json (course.folio):
// closed_date = วันปิดรับสมัคร (ปิดสร้าง/ทำสำเนา + เป็น default ของวันปิดแก้ไข)
// closed_edit_date = override วันปิดแก้ไขถ้าหลักสูตรกำหนดแยก · ไม่มีวัน = ไม่ล็อก
// client time เป็น UX gate — ของจริง server ปฏิเสธ create/update/clone จาก program_project.folio เดียวกัน
// เวลา = simulatedMoment() (เวลาจำลอง ?now= บน staging ตรงกับ server ที่ได้ header เดียวกัน — 2026-10-06)

// ข้อมูลนำเข้าเป็น date-only (00:00:00Z ทุก record ณ ก.ค. 69) = เปิดรับ "ถึงสิ้นวันนั้นเวลาไทย"
// → cutoff จริง = 17:00Z (เที่ยงคืนไทยของวันถัดไป) · เผื่ออนาคตใส่เวลาจริงมา — ใช้ตามนั้น
export const folioClosedCutoff = (iso) => {
  const m = moment.utc(iso);
  return m.format('HH:mm:ss.SSS') === '00:00:00.000' ? m.add(17, 'hours') : m;
};
export const folioApplyClosed = (course) => {
  const closed = _.get(course, 'folio.closed_date');
  return Boolean(closed) && simulatedMoment().isSameOrAfter(folioClosedCutoff(closed));
};
export const folioEditClosed = (course) => {
  const closed = _.get(course, 'folio.closed_edit_date') || _.get(course, 'folio.closed_date');
  return Boolean(closed) && simulatedMoment().isSameOrAfter(folioClosedCutoff(closed));
};
// วันปิดแบบไทยย่อ พ.ศ. 2 หลัก (เช่น "1 ก.พ. 70") — สไตล์เดียวกับบรรทัดอัปเดตล่าสุด; utc กัน timezone เลื่อนวัน
// ปี พ.ศ. ต้องบวกที่ตัวเลขปี ไม่ใช่ moment.add(543,'year') — 29 ก.พ. ค.ศ. อธิกสุรทิน + 543 ตกปีไม่อธิกสุรทิน moment ปัดเป็น 28 ก.พ.
export const thaiYearShort = (m) => String(m.year() + 543).slice(-2);
export const formatFolioClosedDate = (iso) => { const m = moment.utc(iso).locale('th'); return `${m.format('D MMM')} ${thaiYearShort(m)}`; };

// เพดานหน้าแฟ้ม (advisory) — folio_page_limit อาจเป็น string, ช่วง ("2-10"), ข้อความ ("ไม่เกิน 10 หน้า …")
// หรือวันที่เพี้ยนจาก Excel ("ม.ค.-15" = "1-15") → เอาเลขตัวสุดท้ายที่พบ · 0/ว่าง/"ไม่กำหนด" = ไม่มีเพดาน (null)
export const folioPageCap = (course) => {
  const raw = course && (course.folio_page_limit || _.get(course, 'folio.page_limit'));
  if (raw == null) return null;
  const text = String(raw);
  // เลขที่ตามด้วย "หน้า" ก่อน ("ไม่เกิน 3 หน้า A4" = 3 ไม่ใช่ 4, "ไม่เกิน 10 หน้า (TCAS69)" = 10 ไม่ใช่ 69) · ไม่มี → ค่ามากสุดหลังตัด A4/เอ4/เลข 4 หลัก (ปี)
  const byPage = text.match(/(\d+)\s*หน้า/);
  const nums = byPage ? [byPage[1]] : text.replace(/A4|เอ4|\d{4,}/gi, ' ').match(/\d+/g);
  const value = nums && nums.length ? Math.max(...nums.map(Number)) : 0;
  return value > 0 ? value : null;
};

export const isFlSubject = (subjectCode) =>
  subjectCode.startsWith('8') && subjectCode > 82;

export const onScoreChange = ({ scores, scoreProps, fieldName, fnName }) => (e) => {
  const { max, alphanumeric } = _.find(scores, (_score) => _score.fieldName === fieldName) || {};
  const value = `${e.target.value || ''}`;
  if (!alphanumeric) {
    if (value && isNaN(value)) return;
    if (value < 0) return;
    if (value > max) return;
    if (value.includes('.') && value.split('.').pop().length > 2) return;
  }
  scoreProps[fnName(fieldName)](e.target.value.trim());
}

// Gmail throttle ฝั่ง SES (ก.ค. 2026 — ดู tcas65-student-api helpers/email-rate-limiter):
// เมลไป gmail ถึงช้า/ค้างคิว — เด็ก gmail พรีเลือกช่องทาง SMS
// คืน undefined เมื่อไม่เข้าเงื่อนไข = คงพฤติกรรมเดิมของแต่ละหน้า (ยังไม่พรีเลือกอะไร)
export const getDefaultOtpChannel = (profile) => {
  const email = String(_.get(profile, 'email') || '').trim().toLowerCase();
  const isGmail = email.endsWith('@gmail.com') || email.endsWith('@googlemail.com');
  return isGmail && _.get(profile, 'telephone') ? 'mobile' : undefined;
};
